import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Headphones,
  HelpCircle,
  Lightbulb,
  LockKeyhole,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  Volume2,
  X
} from "lucide-react";
import type { Lesson } from "../data/curriculum";
import { pilotContent } from "../data/pilotContent";
import { markLessonCompleted } from "../lib/courseProgress";
import { Brand } from "./Brand";

type LessonPlayerProps = {
  lesson: Lesson;
  onClose: () => void;
};

function formatTime(seconds: number) {
  if (!Number.isFinite(seconds)) return "00:00";
  const minutes = Math.floor(seconds / 60);
  const remaining = Math.floor(seconds % 60);
  return `${String(minutes).padStart(2, "0")}:${String(remaining).padStart(2, "0")}`;
}

export function LessonPlayer({ lesson, onClose }: LessonPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(1200);
  const [showHint, setShowHint] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [audioError, setAudioError] = useState("");
  const content = pilotContent[lesson.id];

  const activePhrase = useMemo(() => {
    return [...content.phrases].reverse().find(phrase => phrase.at <= currentTime) ?? content.phrases[0];
  }, [content.phrases, currentTime]);

  useEffect(() => {
    const saved = Number(localStorage.getItem(`shuni-progress-${lesson.id}`));
    if (Number.isFinite(saved) && saved > 0 && audioRef.current) {
      audioRef.current.currentTime = saved;
      setCurrentTime(saved);
    }
  }, [lesson.id]);

  useEffect(() => {
    localStorage.setItem(`shuni-progress-${lesson.id}`, String(currentTime));
  }, [currentTime, lesson.id]);

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      try {
        await audio.play();
      } catch {
        setAudioError("Tap play once more to let your browser start the lesson audio.");
      }
    } else {
      audio.pause();
    }
  };

  const seekBy = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.min(Math.max(0, audio.currentTime + seconds), duration);
  };

  const replayPhrase = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = activePhrase.at;
    void audio.play();
  };

  return (
    <main className={`player player--${lesson.accent}`}>
      <audio
        ref={audioRef}
        src={lesson.audioSrc}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          markLessonCompleted(lesson.id);
        }}
        onTimeUpdate={event => setCurrentTime(event.currentTarget.currentTime)}
        onLoadedMetadata={event => setDuration(event.currentTarget.duration)}
        onError={() => setAudioError("This pilot audio is not available yet.")}
      />

      <header className="player-header">
        <button type="button" className="icon-button" onClick={onClose} aria-label="Back to course">
          <ArrowLeft aria-hidden="true" />
        </button>
        <Brand compact />
        <span className="player-header__spacer" aria-hidden="true" />
      </header>

      <section className="player-progress" aria-label="Lesson progress">
        <span>{formatTime(currentTime)}</span>
        <input
          type="range"
          min="0"
          max={duration || 1200}
          value={Math.min(currentTime, duration || 1200)}
          onChange={event => {
            if (audioRef.current) audioRef.current.currentTime = Number(event.target.value);
          }}
          aria-label="Seek through lesson"
          style={{ "--progress": `${(currentTime / (duration || 1200)) * 100}%` } as React.CSSProperties}
        />
        <span>{formatTime(duration)}</span>
      </section>

      <section className="player-title">
        <p className="eyebrow">Section {lesson.sectionId} · Lesson {lesson.id}</p>
        <h1>{lesson.title}</h1>
        <p>{content.scene}</p>
      </section>

      <section className="phrase-stage" aria-live="polite">
        <p className="phrase-stage__phonetic">{activePhrase.phonetic}</p>
        <p className="phrase-stage__meaning">{activePhrase.meaning}</p>
      </section>

      <section className="transport-controls" aria-label="Playback controls">
        <button type="button" onClick={() => seekBy(-10)} aria-label="Back 10 seconds">
          <RotateCcw aria-hidden="true" />
          <span>10</span>
        </button>
        <button
          type="button"
          className="transport-controls__main"
          onClick={togglePlayback}
          aria-label={playing ? "Pause lesson" : "Play lesson"}
        >
          {playing ? <Pause fill="currentColor" aria-hidden="true" /> : <Play fill="currentColor" aria-hidden="true" />}
        </button>
        <button type="button" onClick={() => seekBy(10)} aria-label="Forward 10 seconds">
          <RotateCw aria-hidden="true" />
          <span>10</span>
        </button>
      </section>

      {audioError && (
        <p className="audio-error" role="alert">
          <HelpCircle aria-hidden="true" />
          {audioError}
        </p>
      )}

      <section className={`your-turn ${playing ? "your-turn--active" : ""}`}>
        <span className="listening-ring" aria-hidden="true">
          <Headphones />
        </span>
        <div>
          <p className="eyebrow">Your turn</p>
          <h2>{activePhrase.prompt}</h2>
          {showHint && <p className="hint">{activePhrase.phonetic}</p>}
        </div>
      </section>

      <section className="player-actions" aria-label="Lesson actions">
        <button type="button" onClick={togglePlayback}>
          {playing ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
          <span>{playing ? "Pause" : "Continue"}</span>
        </button>
        <button type="button" onClick={replayPhrase}>
          <Volume2 aria-hidden="true" />
          <span>Replay phrase</span>
        </button>
        <button type="button" onClick={() => setShowHint(value => !value)}>
          <Lightbulb aria-hidden="true" />
          <span>{showHint ? "Hide hint" : "I need a hint"}</span>
        </button>
      </section>

      <button
        type="button"
        className="transcript-toggle"
        onClick={() => setShowTranscript(value => !value)}
      >
        {showTranscript ? <X aria-hidden="true" /> : <LockKeyhole aria-hidden="true" />}
        {showTranscript ? "Hide transcript" : "Transcript after lesson"}
        <span>{showTranscript ? "Close" : "Optional"}</span>
      </button>

      {showTranscript && (
        <section className="transcript">
          <div className="transcript__heading">
            <div>
              <p className="eyebrow">End-of-lesson reference</p>
              <h2>Today&apos;s conversation</h2>
            </div>
            <button className="icon-button" type="button" onClick={() => setShowTranscript(false)} aria-label="Close transcript">
              <X aria-hidden="true" />
            </button>
          </div>
          {content.dialogue.map((line, index) => (
            <article className="dialogue-line" key={`${line.speaker}-${index}`}>
              <span>{line.speaker}</span>
              <p>{line.phonetic}</p>
              <small>{line.meaning}</small>
            </article>
          ))}
          <p className="language-note">
            Draft Bangladeshi usage. Before producing the full course, a native Bangla educator
            should review register, pronunciation, and family-specific wording.
          </p>
        </section>
      )}
    </main>
  );
}
